// Bumble Confidential. For Internal Use Only.

type ConsentModeValue = 'granted' | 'denied';

export type ConsentModeState = {
  ad_storage: ConsentModeValue;
  ad_user_data: ConsentModeValue;
  ad_personalization: ConsentModeValue;
  analytics_storage: ConsentModeValue;
};

type VendorConsents = {
  consentedVendors: Array<{ _id: string }>;
};

type TcData = {
  gdprApplies?: boolean;
  eventStatus?: string;
  listenerId?: string | number;
};

type TcfApi = (
  command: string,
  version: number,
  onResult: ((...args: any[]) => void) | null,
  parameter?: unknown,
) => string | number | undefined;

type UspData = { uspString?: string };

type UspApi = (
  command: string,
  version: number,
  onResult: (data: UspData, success: boolean) => void,
) => void;

declare global {
  interface Window {
    __tcfapi?: TcfApi;
    __uspapi?: UspApi;
    __handlePrivacyManagerAction?: () => void;
    gtag?: (...args: unknown[]) => void;
  }
}

const PRIVACY_MANAGER_ACTION_TIMEOUT = 2000;

// Sourcepoint custom vendor ID for Google Ads, shared with bumble.com.
const GOOGLE_ADS_VENDOR_ID = '61fb9e006b4922079c088d5a';

export const GRANT_ALL_CONSENT_MODE: ConsentModeState = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
};

export const DENY_ALL_CONSENT_MODE: ConsentModeState = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
};

// `gtag` is defined in pages/_document.tsx ahead of the GTM snippet, which also
// starts every visitor default-denied. This is what turns categories on, or
// re-confirms denial (e.g. Reject All), each time Sourcepoint reports a consent event.
export function updateConsentMode(state: ConsentModeState) {
  window.gtag?.('consent', 'update', state);
}

// USP v1 string format: "<version><NoticeGiven><OptedOut><LspaCovered>", e.g. "1YNN".
// Position 3 (index 2) is the opt-out-of-sale/share flag: 'Y' = opted out, 'N' = not
// opted out, '-' = not yet determined. '-' counts as not opted out, matching CCPA's
// opt-out-by-default regime - the visitor hasn't acted, so nothing changes yet.
function hasCcpaOptedOut(uspString: string | undefined): boolean {
  return typeof uspString === 'string' && uspString.length >= 3 && uspString[2] === 'Y';
}

// The IAB US Privacy API (unlike TCF) has no addEventListener mechanism - only this
// pull-based getUSPData call - so it has to be invoked whenever the current opt-out
// state is needed. GPP (window.__gpp) is not exposed on this property yet (checked
// live), so this falls back to the US Privacy API; revisit once the Sourcepoint
// account is configured for MSPA/GPP.
//
// `onFailure` controls what happens if Sourcepoint can't confirm the status: on the
// initial page load (no prior signal) "not opted out" is the correct opt-out-by-default
// read, but right after the visitor submits a choice, failing open would silently
// override the action they just took - callers there pass 'deny'.
function readUspOptOutStatus(
  onResult: (hasOptedOut: boolean) => void,
  onFailure: 'grant' | 'deny' = 'grant',
) {
  const fallback = onFailure === 'deny';
  const uspapi = window.__uspapi;

  if (!uspapi) {
    onResult(fallback);

    return;
  }

  try {
    uspapi('getUSPData', 1, (data, success) => {
      onResult(success ? hasCcpaOptedOut(data?.uspString) : fallback);
    });
  } catch {
    onResult(fallback);
  }
}

// Decision carried over from bumble.com (JS-30699): analytics_storage is denied on
// opt-out too, not just the ad signals. TODO(JS-30760): confirm with Legal/Privacy
// that the same call applies to Love Stories.
function applyCcpaConsentState(hasOptedOut: boolean) {
  updateConsentMode(hasOptedOut ? DENY_ALL_CONSENT_MODE : GRANT_ALL_CONSENT_MODE);
}

// Bridges _sp_.config.events.onPrivacyManagerAction (pages/_document.tsx), which fires
// for both GDPR and CCPA privacy manager submissions. GDPR ones are already handled by
// the __tcfapi listener, so this only acts when GDPR doesn't apply: it re-reads the US
// Privacy opt-out status and applies it.
//
// The visitor just submitted a choice, so this can't silently do nothing: a timeout
// fallback checks CCPA anyway if getTCData never calls back, and a failed read fails
// toward deny - overriding the action just taken would be worse than over-denying.
export function handlePrivacyManagerAction() {
  let handled = false;

  const applyCcpaCheck = () => {
    if (handled) {
      return;
    }

    handled = true;
    readUspOptOutStatus(applyCcpaConsentState, 'deny');
  };

  setTimeout(applyCcpaCheck, PRIVACY_MANAGER_ACTION_TIMEOUT);

  window.__tcfapi?.('getTCData', 2, (tcData: TcData | undefined, success: boolean) => {
    if (handled) {
      return;
    }

    if (success && tcData?.gdprApplies) {
      handled = true;

      return;
    }

    applyCcpaCheck();
  });
}

function applyVendorConsents(vendorConsents: VendorConsents, success: boolean) {
  if (!success) {
    // Sourcepoint couldn't confirm vendor consent - stay denied rather than
    // leaving tags in whatever state they were last set to.
    updateConsentMode(DENY_ALL_CONSENT_MODE);

    return;
  }

  const hasGoogleAdsConsent = vendorConsents.consentedVendors.some(
    (vendor) => vendor._id === GOOGLE_ADS_VENDOR_ID,
  );

  // TODO(JS-30759): analytics_storage piggybacks on the Google Ads vendor consent
  // because no separate Google Analytics/GA4 vendor ID is configured in the
  // Sourcepoint property today (same gap as bumble.com, JS-30665). Confirm the real
  // vendor ID with whoever owns the Sourcepoint account config and split this out.
  const value: ConsentModeValue = hasGoogleAdsConsent ? 'granted' : 'denied';

  updateConsentMode({
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
}

// Registered via __tcfapi('addEventListener', ...), so it runs on every
// Sourcepoint consent event (initial load, Reject All, Accept All, or a partial
// save from the privacy manager) - not just once - so a rejection made after tags
// have already loaded still turns them off.
export function handleConsentEvent(tcData: TcData | undefined, success: boolean) {
  if (!success || !tcData) {
    updateConsentMode(DENY_ALL_CONSENT_MODE);

    return;
  }

  if (tcData.gdprApplies) {
    window.__tcfapi?.('getCustomVendorConsents', 2, applyVendorConsents);
  } else if (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'cmpuishown') {
    // Non-GDPR traffic (US/CCPA, rest of world) is an opt-out regime, so consent is
    // assumed - but a returning visitor may already have opted out via the CCPA
    // privacy manager in an earlier session, so check before granting.
    readUspOptOutStatus(applyCcpaConsentState);
  }
}

// Registers the well-known global that pages/_document.tsx's inline Sourcepoint config
// calls from onPrivacyManagerAction; returns a cleanup function.
export function listenForPrivacyManagerActions(): () => void {
  window.__handlePrivacyManagerAction = handlePrivacyManagerAction;

  return () => {
    delete window.__handlePrivacyManagerAction;
  };
}

// Starts listening for consent events; returns a cleanup function.
export function listenForConsentEvents(): () => void {
  let listenerId: TcData['listenerId'];

  // Sourcepoint's addEventListener returns undefined - the listener ID only
  // arrives in the event data, so it has to be captured from the first event.
  window.__tcfapi?.('addEventListener', 2, (tcData: TcData | undefined, success: boolean) => {
    if (success && tcData?.listenerId !== undefined) {
      listenerId = tcData.listenerId;
    }

    handleConsentEvent(tcData, success);
  });

  return () => {
    // Compare against undefined, not falsiness: the first listener's ID is 0.
    if (listenerId !== undefined) {
      // The CMP throws "invalid callback function" if this callback is null.
      window.__tcfapi?.('removeEventListener', 2, () => undefined, listenerId);
    }
  };
}
